import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  CustomerInvoice,
  CustomerInvoiceStatus,
  InvoiceDocumentType,
  PaymentMethod,
} from '@/apis/invoices';
import { TAX_RATE_OPTIONS } from './invoice-form-schema';

// Radix SelectItem forbids value="" — sentinel maps the selectable "empty" choice back to ''
const EMPTY_VALUE = '__empty__';

export function InvoiceSelect({
  value,
  onChange,
  options,
  emptyLabel,
  placeholder,
  disabled,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly { value: string; label: string }[];
  emptyLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <Select
      value={emptyLabel ? value || EMPTY_VALUE : value}
      onValueChange={(v) => onChange(v === EMPTY_VALUE ? '' : v)}
      disabled={disabled}
    >
      <SelectTrigger className={className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {emptyLabel && <SelectItem value={EMPTY_VALUE}>{emptyLabel}</SelectItem>}
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// data-form-error is the scroll target when a submit is blocked by validation.
export function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p data-form-error className="mt-1 text-xs text-danger">
      {message}
    </p>
  );
}

export const TAX_SELECT_OPTIONS = TAX_RATE_OPTIONS.map((t) => ({
  value: t,
  label: `${t}%`,
}));

interface StatusStyle {
  label: string;
  className: string;
}

export const INVOICE_STATUS_STYLE: Record<CustomerInvoiceStatus, StatusStyle> = {
  draft: { label: 'Draft', className: 'text-lightGray' },
  sent: { label: 'Sent', className: 'text-info' },
  paid: { label: 'Paid', className: 'text-success' },
  partially_paid: { label: 'Partially Paid', className: 'text-warning' },
  overdue: { label: 'Overdue', className: 'text-danger' },
  cancelled: { label: 'Cancelled', className: 'text-silver' },
  partially_applied: { label: 'Partially Applied', className: 'text-warning' },
  closed: { label: 'Closed', className: 'text-success' },
  voided: { label: 'Voided', className: 'text-silver' },
};

export function getInvoiceStatusStyle(status: string): StatusStyle {
  return (
    INVOICE_STATUS_STYLE[status as CustomerInvoiceStatus] ?? {
      label: status || 'Unknown',
      className: 'text-lightGray',
    }
  );
}

const INVOICE_ONLY_STATUSES: CustomerInvoiceStatus[] = [
  'draft',
  'sent',
  'paid',
  'partially_paid',
  'overdue',
  'cancelled',
];

const CREDIT_MEMO_STATUSES: CustomerInvoiceStatus[] = [
  'draft',
  'sent',
  'partially_applied',
  'closed',
  'voided',
];

// Listed explicitly rather than derived from the style map: deriving is exactly
// what would let "Voided" appear on an invoice.
const toStatusOptions = (values: CustomerInvoiceStatus[]) =>
  values.map((value) => ({ value, label: INVOICE_STATUS_STYLE[value].label }));

export const INVOICE_STATUS_OPTIONS = toStatusOptions(INVOICE_ONLY_STATUSES);
export const CREDIT_MEMO_STATUS_OPTIONS =
  toStatusOptions(CREDIT_MEMO_STATUSES);

export const statusOptionsFor = (documentType: InvoiceDocumentType) =>
  documentType === 'credit_memo'
    ? CREDIT_MEMO_STATUS_OPTIONS
    : INVOICE_STATUS_OPTIONS;

// US9: sent invoices show a live countdown label derived from dueDate — the
// stored status stays 'sent'; only the display changes.
export function getInvoiceListStatusDisplay(inv: CustomerInvoice): StatusStyle {
  // A credit memo has no due date to count down to.
  if (
    inv.documentType !== 'credit_memo' &&
    inv.status === 'sent' &&
    inv.dueDate
  ) {
    const due = new Date(inv.dueDate);
    if (!isNaN(due.getTime())) {
      const days = Math.ceil((due.getTime() - Date.now()) / 86_400_000);
      return days >= 0
        ? { label: `Due in ${days} day${days === 1 ? '' : 's'}`, className: 'text-info' }
        : { label: `Overdue by ${-days} day${days === -1 ? '' : 's'}`, className: 'text-danger' };
    }
  }
  return getInvoiceStatusStyle(inv.status);
}

export function formatMoney(n: number | string | null | undefined): string {
  const value = Number(n ?? 0);
  const abs = Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return value < 0 ? `-$${abs}` : `$${abs}`;
}

// "12 Feb 2026" — matches the mockup
export function formatListDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const day = d.getDate();
  const month = d.toLocaleString('en-US', { month: 'short' });
  return `${day} ${month} ${d.getFullYear()}`;
}

// The backend tracks no payments ledger yet, so balance is derived from status.
export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  check: 'Check',
  credit_card: 'Credit Card',
  zelle: 'Zelle',
  bank_wire: 'Bank Wire',
  ach: 'ACH',
  cash: 'Cash',
  other: 'Other',
};

export const PAYMENT_METHOD_OPTIONS = (
  Object.entries(PAYMENT_METHOD_LABEL) as [PaymentMethod, string][]
).map(([value, label]) => ({ value, label }));

export function getInvoicePaidSum(inv: CustomerInvoice): number {
  return (inv.payments ?? []).reduce((sum, p) => sum + Number(p.amount ?? 0), 0);
}

export function getInvoiceBalance(inv: CustomerInvoice): number {
  if (inv.status === 'cancelled') return 0;
  return Number(inv.totalAmount ?? 0) - getInvoicePaidSum(inv);
}

// Mirrors the backend's deriveStatus: a credit (negative total) is settled when
// enough has been paid back, so "outstanding" flips sign with the total.
export function hasOutstandingBalance(inv: CustomerInvoice): boolean {
  if (inv.status === 'cancelled') return false;
  const total = Number(inv.totalAmount ?? 0);
  const balance = getInvoiceBalance(inv);
  if (total > 0) return balance > 0;
  if (total < 0) return balance < 0;
  return false;
}

// The client's "How it was paid" field — one line, even when an invoice was
// settled by more than one method.
export function getInvoicePaymentMethodLabel(inv: CustomerInvoice): string {
  const methods = (inv.payments ?? []).map((p) => p.method);
  return [...new Set(methods)]
    .map((m) => PAYMENT_METHOD_LABEL[m] ?? m)
    .join(', ');
}

// Client-side CSV — the BE has no export endpoint. Mirrors the list table columns.
export function downloadInvoicesCsv(rows: CustomerInvoice[], filename: string) {
  const esc = (v: unknown) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = [
    'Date',
    'Invoice#',
    'Order Number',
    'Customer Name',
    'Invoice Status',
    'Due Date',
    'Invoice Amount',
    'Balance',
    'Company Name',
  ];
  const lines = rows.map((inv) =>
    [
      formatListDate(inv.issueDate),
      inv.invoiceNumber,
      inv.orderNumber ?? '',
      inv.customer?.fullName ?? '',
      getInvoiceListStatusDisplay(inv).label,
      formatListDate(inv.dueDate),
      Number(inv.totalAmount ?? 0).toFixed(2),
      getInvoiceBalance(inv).toFixed(2),
      inv.customer?.companyName ?? '',
    ]
      .map(esc)
      .join(','),
  );
  const blob = new Blob([[header.map(esc).join(','), ...lines].join('\n')], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
